import React from 'react';

interface HighlightedTextProps {
    text: string;
    searchQuery: string;
    currentMatchIndex?: number;
    matchCounterRef?: React.MutableRefObject<number>;
    searchMatches?: Array<{ startOffset: number; endOffset: number }>;
}

/**
 * HighlightedText - Renders text with highlighted search matches
 * 
 * Features:
 * - Case-insensitive highlighting
 * - Amber ring for current match, subtle amber wash for others
 * - Preserves original text formatting
 * - Handles multiple matches in same text
 */
export function HighlightedText({
    text,
    searchQuery,
    currentMatchIndex = -1,
    matchCounterRef,
}: HighlightedTextProps) {
    if (!searchQuery || !text) {
        return <>{text}</>;
    }
    const parts: React.ReactNode[] = [];
    const normalizedText = text.toLowerCase();
    const normalizedQuery = searchQuery.toLowerCase().trim();
    
    if (!normalizedQuery) {
        return <>{text}</>;
    }

    let lastIndex = 0;

    // Find all occurrences and split text
    let searchIndex = 0;
    while (searchIndex < normalizedText.length) {
        const index = normalizedText.indexOf(normalizedQuery, searchIndex);
        
        if (index === -1) {
            // Add remaining text
            if (lastIndex < text.length) {
                parts.push(text.substring(lastIndex));
            }
            break;
        }

        // Add text before match
        if (index > lastIndex) {
            parts.push(text.substring(lastIndex, index));
        }
        const matchText = text.substring(index, index + normalizedQuery.length);
        
        // Check if this is the current match
        let isCurrentMatch = false;
        if (matchCounterRef) {
            // Check if this specific match counter equals the current match index
            const currentCounter = matchCounterRef.current;
            isCurrentMatch = currentCounter === currentMatchIndex;
            
            /**
             * Uncomment for detailed match logging
             */
            // console.log(`[HighlightedText] Found match #${currentCounter}:`, {
            //     text: matchText,
            //     position: index,
            //     isCurrentMatch,
            //     currentMatchIndex,
            //     shouldBeHighlighted: currentCounter === currentMatchIndex,
            //     counterBeforeIncrement: matchCounterRef.current,
            //     context: text.substring(Math.max(0, index - 20), Math.min(text.length, index + normalizedQuery.length + 20))
            // });
            
            // Only increment after checking
            matchCounterRef.current += 1;
        }
        
        parts.push(
            <mark
                key={`match-${index}`}
                className={`search-match ${
                    isCurrentMatch
                        ? 'search-match-current bg-amber-400/30 ring-1 ring-amber-400'
                        : 'bg-amber-400/20'
                } text-inherit rounded-sm px-0.5 transition-all duration-200`}
            >
                {matchText}
            </mark>
        );

        lastIndex = index + normalizedQuery.length;
        searchIndex = lastIndex;
    }

    return <>{parts}</>;
};

